/**
 * ApexPowerPanel — collapsible power-monitoring section.
 *
 * Surfaces the Apex EnergyBar's per-outlet amps/watts probes plus a
 * grand-total wattage line. Collapsed by default because most of the
 * time the operator only cares when something looks wrong; expanding
 * shows the per-outlet breakdown.
 *
 * Classification:
 *   - watts probes — `probeType === 'Watts'` (case-insensitive). Summed.
 *   - amps probes  — `probeType === 'Amps'`  (case-insensitive). Shown
 *                    but not totalled (sum-of-amps is meaningless
 *                    without phase info).
 *
 * Falls back to name-suffix detection (`*_W` / `*_A`) so older firmwares
 * that don't surface `probeType` correctly still light up the panel.
 *
 * Renders nothing at all when there are no power probes — the dashboard
 * shouldn't show an empty power section just because an EB832 isn't
 * connected.
 */
import { useState } from 'react';
import clsx from 'clsx';
import { Badge } from './Badge';
import type { ApexProbeSnapshot } from '../lib/types';

export interface ApexPowerPanelProps {
  probes: readonly ApexProbeSnapshot[];
  defaultCollapsed?: boolean;
}

interface ClassifiedProbe {
  probe: ApexProbeSnapshot;
  kind: 'watts' | 'amps';
}

/** Best-effort classification of a probe as Watts/Amps. */
export function classifyPowerProbe(probe: ApexProbeSnapshot): 'watts' | 'amps' | null {
  const t = probe.probeType.toLowerCase();
  if (t === 'watts') return 'watts';
  if (t === 'amps') return 'amps';
  // Fallback: EnergyBar probes are typically named "<outlet>_W" / "<outlet>_A".
  if (/_W$/.test(probe.name)) return 'watts';
  if (/_A$/.test(probe.name)) return 'amps';
  return null;
}

function classifyAll(probes: readonly ApexProbeSnapshot[]): ClassifiedProbe[] {
  const out: ClassifiedProbe[] = [];
  for (const probe of probes) {
    const kind = classifyPowerProbe(probe);
    if (kind !== null) out.push({ probe, kind });
  }
  out.sort((a, b) => a.probe.name.localeCompare(b.probe.name, undefined, { sensitivity: 'base' }));
  return out;
}

export function ApexPowerPanel({ probes, defaultCollapsed = true }: ApexPowerPanelProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const classified = classifyAll(probes);
  if (classified.length === 0) return null;

  const totalWatts = classified.reduce((sum, c) => {
    if (c.kind !== 'watts') return sum;
    return Number.isFinite(c.probe.value) ? sum + c.probe.value : sum;
  }, 0);

  return (
    <section
      data-testid="apex-power-panel"
      data-collapsed={collapsed}
      className="rounded-2xl border border-reef-700/40 bg-reef-900/30"
    >
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
        aria-controls="apex-power-panel-body"
        className={clsx(
          'flex w-full items-center justify-between gap-3 px-4 py-2.5',
          'text-left text-sm font-semibold text-reef-100',
          'hover:bg-reef-800/30 transition-colors rounded-2xl',
        )}
      >
        <span className="flex items-center gap-2">
          <span
            aria-hidden
            className={clsx(
              'inline-block transition-transform text-reef-400',
              collapsed ? 'rotate-0' : 'rotate-90',
            )}
          >
            ▸
          </span>
          Power
        </span>
        <span className="flex items-center gap-2 text-xs font-normal text-reef-300">
          <span
            data-testid="apex-power-total"
            className="tabular-nums text-reef-100"
          >
            {totalWatts.toFixed(1)} W
          </span>
          <Badge variant="neutral">{classified.length}</Badge>
        </span>
      </button>
      {!collapsed ? (
        <div id="apex-power-panel-body" className="grid gap-1 px-3 pb-3 pt-1">
          {classified.map(({ probe, kind }) => (
            <div
              key={probe.name}
              data-testid={`apex-power-row-${probe.name}`}
              className="flex items-center gap-3 rounded-lg border border-reef-700/40 bg-reef-900/40 px-3 py-1.5"
            >
              <span className="flex-1 truncate font-mono text-xs text-reef-100">
                {probe.name}
              </span>
              <Badge variant={kind === 'watts' ? 'info' : 'neutral'}>
                {kind === 'watts' ? 'W' : 'A'}
              </Badge>
              <span className="w-20 text-right tabular-nums text-xs text-reef-200">
                {Number.isFinite(probe.value)
                  ? probe.value.toFixed(kind === 'amps' ? 2 : 1)
                  : '—'}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}